import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createBuilder } from './builder.js';
import {
  CHAIR_NEST,
  ROUND_RADIUS,
  block,
  chairStack,
  clean,
  drum,
  linenStack,
  rod,
  roundFlat,
  roundFolded,
  signEasel,
} from '../models.js';

/**
 * The furniture: every prop that was still a plain box, drawn as the thing it
 * is. The front desk, the lobby's sofas and their coffee tables, the manager's
 * desk and his cabinets, the storage stacks, the tables, benches and counters,
 * the kitchen's line, the carts and cages, the bins, the stage's legs.
 *
 * Visual only, the same contract as the rest of this layer. Each model is built
 * inside the footprint of the box it replaces and no higher than that box,
 * except for the things that stand on top of one (a monitor on a desk, a pot
 * on a range), as things on a table do. The box is hidden; its collider is
 * somewhere else and never notices. `HIDES` is the list, and the tests hold it.
 *
 * What is solid to the game's eyes stays solid to the player's. Perception
 * tests a sight line against these boxes, so a rack or a stack of chairs you
 * could see a person through, when nobody in the game can, would be a lie:
 * the racks and shelves have backs, the stacks a dark core of more stacks,
 * the linen cage is full.
 *
 * One box is not hidden: the linen rack in storage is tinted by the game to
 * show which linen is chosen (`main.js` recolours `prop:store-linen`), so its
 * mesh keeps its name and its material and is handed the folded stacks as its
 * geometry instead.
 */

const FT = 0.3048;
const m = (feet) => feet * FT;

/** Every prop this draws and whose plain box it hides. */
export const HIDES = Object.freeze([
  'lobby-desk',
  'lobby-seat-a',
  'lobby-seat-b',
  'lobby-signboard',
  'service-rope',
  'office-desk',
  'office-cabinet',
  'store-rounds',
  'store-chairs',
  'rack-a',
  'rack-b',
  'rack-c',
  'rack-d',
  'lawn-rounds',
  'lawn-chairs',
  'lawn-table-a',
  'lawn-table-b',
  'kitchen-chairs',
  'kitchen-pass',
  'kitchen-range-a',
  'kitchen-range-b',
  'kitchen-rack',
  'branch-rounds',
  'branch-chairs',
  'branch-risers',
  'branch-signage',
  'shop-bench',
  'shop-shelves',
  'shop-risers',
  'break-table-a',
  'break-bench-a',
  'break-bench-b',
  'break-ice',
  'break-coffee',
  'break-cot',
  'ballroom-linen-cage',
  'stage-leg-a',
  'stage-leg-b',
  'stage-leg-c',
  'stage-leg-d',
  'court-bench-a',
  'court-bench-b',
  'dock-bin',
  'lot-skip',
  'dock-pallet-a',
  'dock-pallet-b',
  'break-vending',
]);

/** Which way each thing faces, where it matters: the side you use it from. */
const FACING = {
  'lobby-desk': [-1, 0],
  'lobby-signboard': [-1, 0],
  'office-desk': [0, 1],
  'office-cabinet': [1, 0],
  'kitchen-pass': [0, 1],
  'kitchen-range-a': [0, 1],
  'kitchen-range-b': [0, -1],
  'kitchen-rack': [1, 0],
  'break-ice': [0, 1],
  'break-coffee': [0, 1],
  'shop-bench': [0, 1],
  'shop-shelves': [0, -1],
  'dock-bin': [-1, 0],
  'lot-skip': [1, 0],
  'break-vending': [0, 1],
  'rack-a': [0, 1],
  'rack-b': [0, -1],
  'rack-c': [0, 1],
  'rack-d': [0, -1],
};

export function createFurniture({ plan, levelObject, bank = null, environment = null }) {
  const group = new THREE.Group();
  group.name = 'look-furniture';
  const b = createBuilder();
  const disposables = [];

  // --- materials -------------------------------------------------------------------
  const cache = new Map();
  const paint = (colour, extra = {}) => {
    const key = `${colour}|${JSON.stringify(extra)}`;
    if (!cache.has(key)) {
      const made = new THREE.MeshStandardMaterial({ color: colour, roughness: 0.7, ...extra });
      if (extra.metalness && environment) made.envMap = environment;
      cache.set(key, made);
      disposables.push(made);
    }
    return cache.get(key);
  };
  /** One of the painted surfaces the building is already made of, or a flat stand-in. */
  const surface = (name, fallback, extra = {}) => {
    const key = `surface:${name}|${JSON.stringify(extra)}`;
    if (!cache.has(key)) {
      const s = bank?.surface(name);
      const made = s
        ? new THREE.MeshStandardMaterial({ map: s.color, roughness: s.roughness, ...extra })
        : new THREE.MeshStandardMaterial({ color: fallback, roughness: 0.7, ...extra });
      if (extra.metalness && environment) made.envMap = environment;
      cache.set(key, made);
      disposables.push(made);
    }
    return cache.get(key);
  };
  const M = {
    walnut: surface('darkWood', 0x5a3d28),
    wood: surface('wood', 0x9a7a52),
    marble: surface('marble', 0xe8e4dc),
    steel: surface('steel', 0x9aa0a6, { metalness: 0.6 }),
    brass: paint(0xc9a35b, { metalness: 0.8, roughness: 0.35 }),
    chrome: paint(0xc8ccd0, { metalness: 0.9, roughness: 0.25 }),
    black: paint(0x222326, { metalness: 0.3, roughness: 0.55 }),
    rubber: paint(0x151515, { roughness: 0.9 }),
    plastic: paint(0x2a2c30, { roughness: 0.5 }),
    screen: paint(0x0a1420, { emissive: 0x284a70, emissiveIntensity: 0.9, roughness: 0.2 }),
    tan: paint(0xc8a97e, { roughness: 1 }),
    claret: paint(0x6a2a2e, { roughness: 1 }),
    linen: paint(0xf2f0ea, { roughness: 0.95 }),
    // The chair's own colour in the catalogue, and a champagne frame.
    cushion: paint(0x5a7a9a, { roughness: 0.92 }),
    chairFrame: paint(0xb8963e, { metalness: 0.7, roughness: 0.35 }),
    roundTop: paint(0x9a7a52, { roughness: 0.85 }),
    roundLegs: paint(0x3a3a3c, { metalness: 0.5, roughness: 0.5 }),
    greyPaint: paint(0x6a7078, { metalness: 0.3, roughness: 0.55 }),
    rackOrange: paint(0xd0702a, { metalness: 0.3, roughness: 0.5 }),
    rackBlue: paint(0x2a4a86, { metalness: 0.3, roughness: 0.5 }),
    carton: paint(0xb08a5a, { roughness: 0.95 }),
    tape: paint(0xd8c89a, { roughness: 0.8 }),
    carpet: paint(0x3a3c40, { roughness: 1 }),
    board: paint(0x3c4658, { roughness: 0.8 }),
    canvas: paint(0x5a6a4a, { roughness: 1 }),
    dumpster: paint(0x2f5a3c, { metalness: 0.2, roughness: 0.7 }),
    skip: paint(0x8a4a2a, { metalness: 0.2, roughness: 0.8 }),
    velvet: paint(0x8c2f3a, { roughness: 1 }),
    pan: paint(0xb8bec4, { metalness: 0.7, roughness: 0.3 }),
    burner: paint(0x101010, { roughness: 0.6 }),
    knob: paint(0x303236, { roughness: 0.4 }),
    white: paint(0xf4f2ee, { roughness: 0.4 }),
    paper: paint(0xf4f0e2, { roughness: 0.9 }),
    plant: paint(0x3a5a2e, { roughness: 1 }),
    tool: paint(0xb02a20, { roughness: 0.5 }),
  };
  const CHAIR = { frame: M.chairFrame, cushion: M.cushion };
  const ROUND = { top: M.roundTop, legs: M.roundLegs };

  // --- placing things --------------------------------------------------------------
  const props = new Map(plan.props.map((p) => [p.id, p]));
  const box = (p) => {
    const [ax, az, bx, bz] = p.rect;
    return {
      minX: m(Math.min(ax, bx)),
      maxX: m(Math.max(ax, bx)),
      minZ: m(Math.min(az, bz)),
      maxZ: m(Math.max(az, bz)),
      base: m(p.base ?? 0),
      top: m((p.base ?? 0) + p.height),
    };
  };
  /**
   * A prop's own frame: centred on its footprint, on its base, turned so its
   * local +z is the way it faces. `W` is its width across that, `D` its depth
   * along it, `H` its height.
   */
  const frameOf = (p) => {
    const r = box(p);
    const facing = FACING[p.id] ?? [0, 1];
    const sx = r.maxX - r.minX;
    const sz = r.maxZ - r.minZ;
    const across = facing[0] !== 0;
    return {
      cx: (r.minX + r.maxX) / 2,
      cz: (r.minZ + r.maxZ) / 2,
      base: r.base,
      yaw: Math.atan2(facing[0], facing[1]),
      W: across ? sz : sx,
      D: across ? sx : sz,
      H: r.top - r.base,
    };
  };
  const hidden = [];
  const hide = (id) => {
    const mesh = levelObject.getObjectByName(`prop:${id}`);
    if (mesh) {
      mesh.visible = false;
      hidden.push(id);
    }
  };
  /** Puts a geometry built in a prop's frame into the world. */
  const put = (material, g, f) => {
    if (f.yaw) g.rotateY(f.yaw);
    g.translate(f.cx, f.base, f.cz);
    b.add(material, g);
  };
  const slab = (material, f, sx, sy, sz, x, y, z) => put(material, block(sx, sy, sz, x, y, z), f);
  /** A model from `models.js`, at x, y, z in the frame, turned `yaw` in it. */
  const model = (parts, materials, f, x = 0, y = 0, z = 0, yaw = 0) => {
    for (const [part, list] of Object.entries(parts)) {
      for (const g of list) {
        if (yaw) g.rotateY(yaw);
        put(materials[part], g.translate(x, y, z), f);
      }
    }
  };
  /** Casters: a swivel, a wheel. */
  const caster = (f, x, z, r = 0.05) => {
    slab(M.black, f, 0.05, 0.03, 0.05, x, r * 2 + 0.015, z);
    put(M.rubber, new THREE.CylinderGeometry(r, r, 0.03, 12).rotateZ(Math.PI / 2).translate(x, r, z), f);
  };
  /** Rows of chair stacks filling `W` by `D`, as high as `H` allows. */
  const chairStacks = (f, W = f.W, D = f.D, H = f.H) => {
    const count = Math.max(1, Math.floor((H - 0.92) / CHAIR_NEST) + 1);
    // The stacks further in, as a dark mass seen between the legs: the box is
    // solid to anybody's eyes, so what is drawn has to be.
    slab(M.cushion, f, Math.max(0.1, W - 0.5), H * 0.9, Math.max(0.1, D - 0.45), 0, H * 0.45, 0);
    const nx = Math.max(1, Math.floor(W / 0.58));
    const nz = Math.max(1, Math.floor(D / 0.6));
    for (let i = 0; i < nx; i += 1) {
      for (let k = 0; k < nz; k += 1) {
        const x = -W / 2 + (W / nx) * (i + 0.5);
        const z = -D / 2 + (D / nz) * (k + 0.5) + 0.02;
        model(chairStack(count), CHAIR, f, x, 0, z);
      }
    }
  };
  const each = (id, draw) => {
    const p = props.get(id);
    if (!p) return;
    hide(id);
    draw(frameOf(p), p);
  };

  // --- front of house -----------------------------------------------------------
  // The front desk: walnut panelled front with a brass kick and a marble
  // transaction top toward the guests, returns at either end, and behind it the
  // work surface with the screens and a back counter against the wall.
  each('lobby-desk', (f) => {
    const { W, D, H } = f;
    const front = D / 2;
    const deep = 0.75;
    const zf = front - deep / 2;
    slab(M.walnut, f, W, H - 0.06, deep, 0, (H - 0.06) / 2, zf);
    for (let i = 0; i < Math.floor(W / 1.2); i += 1) {
      const x = -W / 2 + 0.6 + i * 1.2 + (W % 1.2) / 2;
      slab(M.walnut, f, 1.0, H * 0.6, 0.03, x, H * 0.46, front + 0.012);
      slab(M.brass, f, 1.0, 0.012, 0.01, x, H * 0.76 + 0.01, front + 0.03);
    }
    slab(M.brass, f, W, 0.12, 0.02, 0, 0.06, front + 0.01);
    slab(M.marble, f, W + 0.04, 0.05, deep + 0.1, 0, H - 0.025, zf + 0.03);
    // The returns, and the low work surface behind the front.
    for (const sx of [-1, 1]) slab(M.walnut, f, 0.6, H - 0.06, D - deep, sx * (W / 2 - 0.3), (H - 0.06) / 2, -deep / 2);
    const work = 0.76;
    slab(M.walnut, f, W - 1.2, work - 0.04, 0.55, 0, (work - 0.04) / 2, front - deep - 0.28);
    slab(M.marble, f, W - 1.2, 0.04, 0.6, 0, work - 0.02, front - deep - 0.3);
    // The back counter against the wall.
    slab(M.walnut, f, W - 1.2, 0.9, 0.55, 0, 0.45, -D / 2 + 0.3);
    slab(M.marble, f, W - 1.2, 0.03, 0.6, 0, 0.915, -D / 2 + 0.3);
    // Screens, keyboards and a phone at each station, facing the staff side.
    const stations = Math.max(2, Math.floor((W - 1.4) / 2.2));
    for (let i = 0; i < stations; i += 1) {
      const x = -W / 2 + 0.7 + ((W - 1.4) / stations) * (i + 0.5);
      const z = front - deep - 0.2;
      slab(M.plastic, f, 0.2, 0.02, 0.16, x, work + 0.01, z);
      slab(M.plastic, f, 0.04, 0.3, 0.04, x, work + 0.15, z);
      slab(M.plastic, f, 0.56, 0.34, 0.03, x, work + 0.38, z);
      slab(M.screen, f, 0.52, 0.3, 0.005, x, work + 0.38, z - 0.018);
      slab(M.plastic, f, 0.44, 0.02, 0.14, x, work + 0.01, z - 0.26);
      slab(M.plastic, f, 0.18, 0.06, 0.2, x + 0.45, work + 0.03, z - 0.1);
      // Key-card encoder, and a bell on the transaction top.
      slab(M.black, f, 0.12, 0.08, 0.16, x - 0.45, work + 0.04, z - 0.1);
      put(M.brass, new THREE.SphereGeometry(0.045, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2).translate(x + 0.5, H, zf + 0.15), f);
    }
  });

  // The lounge: two low settees facing across a walnut coffee table, on each
  // rug. Low-slung, the kind a hotel lobby buys: seat, back and arms all under
  // the top of the box you bump into, so standing on one you stand on it.
  for (const id of ['lobby-seat-a', 'lobby-seat-b']) {
    each(id, (f) => {
      const { W, D, H } = f;
      const seat = H * 0.66;
      const depth = 0.86;
      const length = Math.min(W - 0.3, 2.3);
      for (const sz of [-1, 1]) {
        const z = sz * (D / 2 - depth / 2);
        const out = -sz; // A settee faces into the middle.
        // Plinth, seat cushions, a low back, arms level with it.
        slab(M.walnut, f, length, 0.07, depth - 0.04, 0, 0.035, z);
        const cushions = 3;
        const cw = (length - 0.3) / cushions;
        for (let c = 0; c < cushions; c += 1) {
          const x = -length / 2 + 0.15 + cw * (c + 0.5);
          slab(M.tan, f, cw - 0.02, seat - 0.07, depth - 0.3, x, 0.07 + (seat - 0.07) / 2, z + out * 0.1);
          slab(M.tan, f, cw - 0.02, H - seat - 0.02, 0.16, x, seat + (H - seat - 0.02) / 2, z - out * (depth / 2 - 0.2));
        }
        slab(M.tan, f, length - 0.28, H - 0.07, 0.13, 0, 0.07 + (H - 0.07) / 2, z - out * (depth / 2 - 0.07));
        for (const sx of [-1, 1]) slab(M.tan, f, 0.14, H - 0.1, depth - 0.04, sx * (length / 2 - 0.07), 0.07 + (H - 0.1) / 2, z);
        // Two claret cushions in the corners, leaning on the back.
        for (const sx of [-1, 1]) {
          const g = block(0.28, 0.14, 0.07, 0, 0, 0).rotateX(out * 0.3).translate(sx * (length / 2 - 0.34), seat + 0.065, z - out * (depth / 2 - 0.3));
          put(M.claret, g, f);
        }
      }
      // The coffee table between them: walnut top on brass legs, a tray on it.
      const tw = Math.min(1.3, W * 0.4);
      const td = Math.max(0.4, D - depth * 2 - 0.2);
      const top = Math.min(0.38, H - 0.08);
      slab(M.walnut, f, tw, 0.04, td, 0, top, 0);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) slab(M.brass, f, 0.03, top - 0.02, 0.03, sx * (tw / 2 - 0.05), (top - 0.02) / 2, sz * (td / 2 - 0.05));
      slab(M.brass, f, tw - 0.1, 0.015, td - 0.1, 0, 0.1, 0);
      slab(M.brass, f, 0.4, 0.015, 0.26, 0.2, top + 0.028, 0);
      put(M.white, drum(0.05, 0.04, 0.05, -0.25, top + 0.045, 0.02, 12), f);
      put(M.plant, new THREE.SphereGeometry(0.06, 10, 6).scale(1, 0.6, 1).translate(-0.25, H - 0.035, 0.02), f);
    });
  }

  // The welcome sign on its easel, beside the desk.
  each('lobby-signboard', (f) => {
    model(signEasel(f.W * 0.9, f.H), { board: M.board, frame: M.brass }, f, 0, 0, -f.D * 0.1);
    slab(M.brass, f, f.W * 0.62, 0.02, 0.01, 0, f.H - 0.18, 0.02);
  });

  // The rope across the service corridor: two brass stanchions, a velvet rope.
  each('service-rope', (f) => {
    const { W, H } = f;
    const span = W - 0.3;
    for (const sx of [-1, 1]) {
      put(M.brass, drum(0.17, 0.17, 0.03, sx * span / 2, 0.015, 0, 20), f);
      put(M.brass, drum(0.025, 0.025, H * 0.34, sx * span / 2, H * 0.17, 0, 10), f);
      put(M.brass, new THREE.SphereGeometry(0.05, 12, 8).translate(sx * span / 2, H * 0.35, 0), f);
    }
    const points = [];
    for (let i = 0; i <= 12; i += 1) {
      const t = i / 12;
      points.push(new THREE.Vector3(-span / 2 + span * t, H * 0.33 - Math.sin(t * Math.PI) * 0.14, 0));
    }
    put(M.velvet, new THREE.TubeGeometry(new THREE.CatmullRomCurve3(points), 16, 0.022, 6), f);
  });

  // --- the manager's office ------------------------------------------------------
  // A walnut desk on two drawer pedestals, a monitor and keyboard, a lamp, the
  // day's papers, a phone.
  each('office-desk', (f) => {
    const { W, D, H } = f;
    slab(M.walnut, f, W, 0.04, D, 0, H - 0.02, 0);
    const ped = 0.46;
    for (const sx of [-1, 1]) {
      const x = sx * (W / 2 - ped / 2 - 0.02);
      slab(M.walnut, f, ped, H - 0.04, D - 0.06, x, (H - 0.04) / 2, 0);
      const drawers = [0.16, 0.16, 0.34];
      let y = H - 0.06;
      for (const hgt of drawers) {
        slab(M.walnut, f, ped - 0.04, hgt - 0.02, 0.02, x, y - hgt / 2, D / 2 - 0.02);
        slab(M.brass, f, 0.12, 0.015, 0.02, x, y - Math.min(0.06, hgt / 3), D / 2 + 0.005);
        y -= hgt;
      }
    }
    slab(M.walnut, f, W - ped * 2, H * 0.5, 0.025, 0, H * 0.7, -D / 2 + 0.04);
    // The centre drawer over the kneehole.
    slab(M.walnut, f, W - ped * 2 - 0.08, 0.08, D - 0.1, 0, H - 0.08, 0);
    slab(M.brass, f, 0.12, 0.012, 0.02, 0, H - 0.08, D / 2 - 0.04);
    // The monitor, facing whoever sits at the desk.
    const y = H;
    slab(M.plastic, f, 0.22, 0.02, 0.18, 0, y + 0.01, -D / 2 + 0.25);
    slab(M.plastic, f, 0.05, 0.3, 0.04, 0, y + 0.16, -D / 2 + 0.22);
    slab(M.plastic, f, 0.6, 0.36, 0.03, 0, y + 0.4, -D / 2 + 0.25);
    slab(M.screen, f, 0.56, 0.32, 0.005, 0, y + 0.4, -D / 2 + 0.268);
    slab(M.plastic, f, 0.44, 0.02, 0.14, 0, y + 0.01, 0.05);
    slab(M.plastic, f, 0.06, 0.02, 0.1, 0.32, y + 0.01, 0.06);
    // A green-shaded banker's lamp, a phone, papers, a nameplate.
    const lx = -W / 2 + 0.3;
    put(M.brass, drum(0.08, 0.09, 0.02, lx, y + 0.01, -D / 2 + 0.25, 16), f);
    put(M.brass, drum(0.012, 0.012, 0.3, lx, y + 0.16, -D / 2 + 0.25, 8), f);
    put(paint(0x1e5a3a, { roughness: 0.3 }), new THREE.CylinderGeometry(0.06, 0.12, 0.08, 16, 1, true, 0, Math.PI).rotateX(-Math.PI / 2).translate(lx, y + 0.32, -D / 2 + 0.2), f);
    slab(M.plastic, f, 0.2, 0.06, 0.2, W / 2 - 0.3, y + 0.03, -D / 2 + 0.3);
    for (let i = 0; i < 3; i += 1) slab(M.paper, f, 0.22, 0.004 + i * 0.004, 0.3, W / 2 - 0.35 + i * 0.02, y + 0.004 + i * 0.004, 0.1 - i * 0.01);
    slab(M.brass, f, 0.26, 0.05, 0.02, -0.4, y + 0.025, D / 2 - 0.08);
  });

  // The cabinets: three lateral files side by side, three drawers each.
  each('office-cabinet', (f) => {
    const { W, D, H } = f;
    const n = 3;
    const w = W / n;
    for (let i = 0; i < n; i += 1) {
      const x = -W / 2 + w * (i + 0.5);
      slab(M.greyPaint, f, w - 0.01, H, D - 0.02, x, H / 2, -0.01);
      for (let d = 0; d < 3; d += 1) {
        const y = H - 0.05 - (H - 0.1) / 3 * (d + 0.5);
        slab(M.greyPaint, f, w - 0.05, (H - 0.1) / 3 - 0.02, 0.02, x, y, D / 2 - 0.01);
        slab(M.chrome, f, 0.22, 0.02, 0.03, x, y + 0.08, D / 2 + 0.01);
        slab(M.paper, f, 0.08, 0.035, 0.005, x, y + 0.14, D / 2 + 0.002);
      }
    }
    // A plant and a box of files on top.
    put(M.walnut, drum(0.12, 0.1, 0.2, -W / 2 + 0.25, H + 0.1, 0, 12), f);
    put(M.plant, new THREE.SphereGeometry(0.22, 10, 7).scale(1, 1.2, 1).translate(-W / 2 + 0.25, H + 0.38, 0), f);
    slab(M.carton, f, 0.4, 0.26, 0.32, W / 2 - 0.35, H + 0.13, 0);
  });

  // --- storage ------------------------------------------------------------------------
  // Rounds laid flat, four stacks of them on dollies.
  each('store-rounds', (f) => {
    const { W, D, H } = f;
    const nx = Math.max(1, Math.floor(W / (ROUND_RADIUS * 2)));
    const nz = Math.max(1, Math.floor(D / (ROUND_RADIUS * 2)));
    const dolly = 0.12;
    const thick = 0.058;
    const count = Math.floor((H - dolly) / thick);
    for (let i = 0; i < nx; i += 1) {
      for (let k = 0; k < nz; k += 1) {
        const x = -W / 2 + (W / nx) * (i + 0.5);
        const z = -D / 2 + (D / nz) * (k + 0.5);
        slab(M.black, f, 1.1, 0.04, 1.1, x, dolly - 0.04, z);
        for (const [dx, dz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) caster(f, x + dx * 0.48, z + dz * 0.48, 0.035);
        for (let n = 0; n < count; n += 1) model(roundFlat(thick), ROUND, f, x + ((n % 3) - 1) * 0.012, dolly + n * thick, z + ((n % 2) - 0.5) * 0.015, n * 0.4);
      }
    }
  });

  // Chairs, stacked as high as a stack goes before it tips.
  each('store-chairs', (f) => chairStacks(f));

  // The linen: folded stacks on a steel platform. Drawn as the rack's own
  // geometry, so the game's tint of it still says which linen is chosen.
  const linenProp = props.get('store-linen');
  const linenMesh = levelObject.getObjectByName('prop:store-linen');
  let linenGeometry = null;
  if (linenProp && linenMesh) {
    const f = frameOf(linenProp);
    const { W, D, H } = f;
    const deck = 0.28;
    slab(M.steel, f, W, 0.04, D, 0, deck - 0.02, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 0, 1]) slab(M.steel, f, 0.05, deck - 0.04, 0.05, sx * (W / 2 - 0.05), (deck - 0.04) / 2, sz * (D / 2 - 0.05));
    const bw = 0.62;
    const bd = 0.46;
    const nx = Math.floor(W / (bw + 0.08));
    const nz = Math.floor(D / (bd + 0.1));
    const count = Math.floor((H - deck) / 0.042);
    const pieces = [];
    for (let i = 0; i < nx; i += 1) {
      for (let k = 0; k < nz; k += 1) {
        const x = -W / 2 + (W / nx) * (i + 0.5);
        const z = -D / 2 + (D / nz) * (k + 0.5);
        const height = count - ((i * 3 + k * 5) % 4);
        for (const g of linenStack(height, bw, bd).linen) pieces.push(g.translate(x, deck, z));
      }
    }
    // Into the mesh's own frame: the box's centre is its origin.
    const list = pieces.map((g) => clean(g.translate(f.cx - linenMesh.position.x, f.base - linenMesh.position.y, f.cz - linenMesh.position.z)));
    linenGeometry = mergeAll(list);
    linenMesh.geometry = linenGeometry;
  }

  // Pallet racking: orange beams on blue uprights, three levels of cartons.
  for (const id of ['rack-a', 'rack-b', 'rack-c', 'rack-d']) {
    each(id, (f) => {
      const { W, D, H } = f;
      const bays = Math.max(1, Math.round(W / 2.7));
      const bay = W / bays;
      const levels = [0.12, H * 0.36, H * 0.69, H - 0.05];
      for (let i = 0; i <= bays; i += 1) {
        const x = -W / 2 + bay * i + (i === 0 ? 0.05 : i === bays ? -0.05 : 0);
        for (const sz of [-1, 1]) slab(M.rackBlue, f, 0.08, H, 0.08, x, H / 2, sz * (D / 2 - 0.05));
        for (let k = 0; k < 5; k += 1) put(M.rackBlue, rod(0.012, [x, 0.2 + k * (H / 5), -D / 2 + 0.05], [x, 0.2 + (k + 0.5) * (H / 5), D / 2 - 0.05]), f);
      }
      // A mesh back down the middle, so the rack is as solid to the eye as
      // it is to anybody looking for you behind it.
      slab(M.greyPaint, f, W - 0.1, H - 0.1, 0.02, 0, H / 2, 0);
      for (const y of levels.slice(1)) {
        for (const sz of [-1, 1]) slab(M.rackOrange, f, W, 0.1, 0.05, 0, y - 0.05, sz * (D / 2 - 0.05));
        slab(M.greyPaint, f, W - 0.1, 0.02, D - 0.1, 0, y - 0.01, 0);
      }
      // Cartons on every level, a few gaps, the tops of the top level the rack's own top.
      for (let lv = 0; lv < 3; lv += 1) {
        const y0 = levels[lv];
        const room = levels[lv + 1] - y0 - 0.12;
        for (let i = 0; i < bays; i += 1) {
          for (let c = 0; c < 3; c += 1) {
            const w = bay / 3 - 0.08;
            const x = -W / 2 + bay * i + bay / 3 * (c + 0.5);
            const hgt = lv === 2 ? room : room * (0.8 + ((i + c + lv) % 3) * 0.07);
            // Two deep, one either side of the back.
            for (const sz of [-1, 1]) {
              slab(M.carton, f, w, hgt, D / 2 - 0.14, x, y0 + hgt / 2, sz * (D / 4 + 0.02));
              slab(M.tape, f, 0.06, 0.004, D / 2 - 0.13, x, y0 + hgt + 0.002, sz * (D / 4 + 0.02));
            }
          }
        }
      }
    });
  }

  // --- the lawn, the kitchen, the corridor: stacks staged to go out ------------
  // Flip-top rounds folded in half and stood on edge in a caddy.
  const caddy = (f) => {
    const { W, D, H } = f;
    const thick = 0.07;
    const n = Math.max(1, Math.floor((D - 0.12) / (thick + 0.018)));
    slab(M.black, f, W - 0.05, 0.05, D, 0, 0.09, 0);
    for (const sx of [-1, 1]) {
      slab(M.black, f, 0.04, Math.min(H, 0.7), 0.04, sx * (W / 2 - 0.05), Math.min(H, 0.7) / 2 + 0.1, 0);
      for (const sz of [-1, 1]) caster(f, sx * (W / 2 - 0.12), sz * (D / 2 - 0.1), 0.04);
    }
    const height = Math.min(ROUND_RADIUS, H - 0.12);
    for (let i = 0; i < n; i += 1) {
      const z = -D / 2 + 0.06 + (thick + 0.018) * (i + 0.5);
      const parts = roundFolded(thick);
      for (const g of parts.top) g.scale(Math.min(1, (W - 0.1) / (ROUND_RADIUS * 2)), height / ROUND_RADIUS, 1);
      model(parts, ROUND, f, 0, 0.115, z);
    }
  };
  each('lawn-rounds', caddy);
  each('lawn-chairs', (f) => chairStacks(f));
  each('kitchen-chairs', (f) => chairStacks(f));
  each('branch-chairs', (f) => chairStacks(f));
  // In the corridor the rounds are folded and laid flat, ready to go.
  each('branch-rounds', (f) => {
    const thick = 0.07;
    const n = Math.max(1, Math.floor(f.H / thick));
    for (let i = 0; i < n; i += 1) {
      const parts = roundFolded(thick);
      for (const list of Object.values(parts)) {
        for (const g of list) {
          g.rotateX(-Math.PI / 2).translate(0, thick / 2 + i * thick, ROUND_RADIUS / 2);
          g.scale(Math.min(1, f.W / (ROUND_RADIUS * 2)), 1, Math.min(1, f.D / ROUND_RADIUS));
        }
      }
      model(parts, ROUND, f, 0, 0, 0, i % 2 ? 0.02 : -0.02);
    }
  });
  // Risers: carpeted decks, legs folded, stacked.
  const riserStack = (f) => {
    const { W, D, H } = f;
    const n = Math.max(1, Math.round(H / 0.16));
    const t = H / n;
    for (let i = 0; i < n; i += 1) {
      const y = i * t;
      slab(M.wood, f, W - 0.02 - (i % 2) * 0.03, t * 0.62, D - 0.02, (i % 2) * 0.015, y + t * 0.31, 0);
      slab(M.carpet, f, W - 0.04 - (i % 2) * 0.03, t * 0.3, D - 0.04, (i % 2) * 0.015, y + t * 0.77, 0);
      slab(M.black, f, W - 0.02, 0.012, 0.04, 0, y + t * 0.95, D / 2 - 0.03);
    }
  };
  each('branch-risers', riserStack);
  each('shop-risers', riserStack);
  // Signage boards, stood on edge in a floor rack.
  each('branch-signage', (f) => {
    const { W, D, H } = f;
    const n = 5;
    slab(M.black, f, W, 0.05, D, 0, 0.025, 0);
    for (const sx of [-1, 1]) slab(M.black, f, 0.04, H * 0.4, D, sx * (W / 2 - 0.04), H * 0.2 + 0.05, 0);
    for (let i = 0; i < n; i += 1) {
      const z = -D / 2 + 0.1 + ((D - 0.2) / n) * (i + 0.5);
      slab(i % 2 ? M.board : paint(0x2a3448, { roughness: 0.8 }), f, W * 0.8 - (i % 3) * 0.1, H - 0.1 - (i % 2) * 0.12, 0.03, 0, (H - 0.1 - (i % 2) * 0.12) / 2 + 0.05, z);
      slab(M.brass, f, W * 0.8 - (i % 3) * 0.1, 0.02, 0.035, 0, H - 0.07 - (i % 2) * 0.12, z);
    }
  });
  // Banquet tables on the lawn, dressed in white to the ground.
  for (const id of ['lawn-table-a', 'lawn-table-b']) {
    each(id, (f) => {
      const { W, D, H } = f;
      slab(M.linen, f, W, 0.02, D, 0, H - 0.01, 0);
      for (const sz of [-1, 1]) {
        const g = block(W, H - 0.04, 0.01, 0, (H - 0.04) / 2 + 0.03, sz * (D / 2 + 0.005));
        put(M.linen, g, f);
      }
      for (const sx of [-1, 1]) put(M.linen, block(0.01, H - 0.04, D, sx * (W / 2 + 0.005), (H - 0.04) / 2 + 0.03, 0), f);
      // A few pleats down the long sides.
      for (let i = 0; i < Math.floor(W / 0.3); i += 1) for (const sz of [-1, 1]) slab(M.linen, f, 0.02, H - 0.06, 0.02, -W / 2 + 0.15 + i * 0.3, (H - 0.06) / 2 + 0.03, sz * (D / 2 + 0.012));
      // Pitchers and stacked glasses, waiting.
      for (let i = 0; i < 3; i += 1) put(paint(0xdfe8ec, { roughness: 0.05, transparent: true, opacity: 0.6 }), drum(0.06, 0.05, 0.22, -W / 3 + i * (W / 3), H + 0.11, 0, 12), f);
    });
  }

  // --- the kitchen --------------------------------------------------------------
  // The pass: a steel worktop on legs with an undershelf of plates and tubs.
  // The heat lamps over it are the dressing's.
  each('kitchen-pass', (f) => {
    const { W, D, H } = f;
    slab(M.steel, f, W, 0.05, D, 0, H - 0.025, 0);
    slab(M.steel, f, W - 0.02, 0.1, 0.03, 0, H - 0.1, D / 2 - 0.02);
    slab(M.steel, f, W - 0.02, 0.1, 0.03, 0, H - 0.1, -D / 2 + 0.02);
    for (let i = 0; i < 3; i += 1) for (const sz of [-1, 1]) put(M.chrome, drum(0.022, 0.022, H - 0.05, -W / 2 + 0.06 + (W - 0.12) * i / 2, (H - 0.05) / 2, sz * (D / 2 - 0.06), 10), f);
    slab(M.steel, f, W - 0.1, 0.03, D - 0.1, 0, 0.26, 0);
    for (let i = 0; i < 4; i += 1) {
      const x = -W / 2 + 0.5 + i * ((W - 1) / 3);
      for (let k = 0; k < 8; k += 1) put(M.white, drum(0.13, 0.11, 0.018, x, 0.285 + k * 0.02, -0.3, 20), f);
      slab(M.plastic, f, 0.5, 0.18, 0.38, x, 0.365, 0.35);
    }
  });
  // The ranges: burners and grates on top, oven doors and a row of knobs
  // toward the cook.
  for (const id of ['kitchen-range-a', 'kitchen-range-b']) {
    each(id, (f) => {
      const { W, D, H } = f;
      const units = Math.max(1, Math.round(W / 1.5));
      const u = W / units;
      for (let i = 0; i < units; i += 1) {
        const x = -W / 2 + u * (i + 0.5);
        slab(M.steel, f, u - 0.01, H - 0.12, D, x, (H - 0.12) / 2 + 0.12, 0);
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) slab(M.black, f, 0.06, 0.12, 0.06, x + sx * (u / 2 - 0.08), 0.06, sz * (D / 2 - 0.1));
        // The oven door, its handle, the knob rail.
        slab(M.steel, f, u - 0.14, H * 0.45, 0.03, x, H * 0.38, D / 2 + 0.005);
        put(M.chrome, drum(0.016, 0.016, u - 0.3, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(x, H * 0.58, D / 2 + 0.06), f);
        slab(M.steel, f, u - 0.02, 0.1, 0.1, x, H - 0.12, D / 2 - 0.02);
        for (let k = 0; k < 6; k += 1) put(M.knob, drum(0.022, 0.022, 0.03, 0, 0, 0, 10).rotateX(Math.PI / 2).translate(x - u / 2 + 0.12 + k * ((u - 0.24) / 5), H - 0.12, D / 2 + 0.045), f);
        // Grates and burners, and a stock pot on one.
        slab(M.burner, f, u - 0.04, 0.03, D - 0.3, x, H + 0.015, 0.05);
        for (let g = 0; g < 4; g += 1) put(M.burner, drum(0.1, 0.1, 0.02, x + (g % 2 ? 0.25 : -0.25) * u / 1.5, H + 0.04, (g < 2 ? -0.25 : 0.3) * D / 1.83, 12), f);
        if (i % 2 === 0) put(M.pan, drum(0.17, 0.16, 0.3, x - 0.25 * u / 1.5, H + 0.2, -0.25 * D / 1.83, 16), f);
        slab(M.steel, f, u - 0.01, 0.4, 0.03, x, H + 0.2, -D / 2 + 0.015);
      }
    });
  }
  // Speed racks of sheet pans.
  each('kitchen-rack', (f) => {
    const { W, D, H } = f;
    slab(M.steel, f, W - 0.05, H - 0.1, 0.02, 0, H / 2 + 0.05, -D / 2 + 0.03);
    const racks = Math.max(1, Math.round(W / 0.7));
    const w = W / racks;
    for (let i = 0; i < racks; i += 1) {
      const x = -W / 2 + w * (i + 0.5);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) put(M.chrome, drum(0.015, 0.015, H - 0.1, x + sx * (w / 2 - 0.05), (H - 0.1) / 2 + 0.1, sz * (D / 2 - 0.05), 8), f);
      for (const sx of [-1, 1]) for (const sz of [-1, 1]) caster(f, x + sx * (w / 2 - 0.05), sz * (D / 2 - 0.05), 0.04);
      slab(M.chrome, f, w - 0.05, 0.02, D - 0.05, x, H - 0.02, 0);
      const slots = Math.floor((H - 0.3) / 0.1);
      for (let k = 0; k < slots; k += 1) if ((k * 5 + i * 3) % 7 !== 0) slab(M.pan, f, w - 0.14, 0.02, D - 0.1, x, 0.25 + k * 0.1, 0);
    }
  });

  // --- the break room -----------------------------------------------------------
  each('break-table-a', (f) => {
    const { W, D, H } = f;
    slab(M.wood, f, W, 0.04, D, 0, H - 0.02, 0);
    for (const sx of [-1, 1]) {
      slab(M.black, f, 0.05, H - 0.04, 0.05, sx * (W / 2 - 0.3), (H - 0.04) / 2, 0);
      slab(M.black, f, 0.06, 0.04, D - 0.3, sx * (W / 2 - 0.3), 0.02, 0);
    }
    slab(M.white, f, 0.1, 0.1, 0.1, 0.4, H + 0.05, 0.1);
    put(M.white, drum(0.045, 0.04, 0.1, -0.5, H + 0.05, -0.2, 10), f);
  });
  const bench = (f) => {
    const { W, D, H } = f;
    for (let i = 0; i < 3; i += 1) slab(M.wood, f, W - 0.04, 0.035, D / 3 - 0.015, 0, H - 0.018, -D / 2 + D / 3 * (i + 0.5));
    for (const sx of [-1, 0, 1]) {
      const x = sx * (W / 2 - 0.3);
      slab(M.black, f, 0.05, H - 0.04, D - 0.06, x, (H - 0.04) / 2, 0);
    }
  };
  each('break-bench-a', bench);
  each('break-bench-b', bench);
  each('court-bench-a', bench);
  each('court-bench-b', bench);
  // The ice machine: a steel bin with a sloped door, the ice-maker head on top.
  each('break-ice', (f) => {
    const { W, D, H } = f;
    const bin = H * 0.5;
    slab(M.steel, f, W - 0.02, bin, D - 0.02, 0, bin / 2 + 0.1, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) slab(M.black, f, 0.05, 0.1, 0.05, sx * (W / 2 - 0.08), 0.05, sz * (D / 2 - 0.08));
    const door = block(W - 0.2, 0.3, 0.03, 0, 0, 0).rotateX(-0.35).translate(0, bin - 0.1, D / 2 - 0.02);
    put(M.steel, door, f);
    put(M.chrome, drum(0.015, 0.015, W - 0.4, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(0, bin + 0.05, D / 2 + 0.03), f);
    slab(M.steel, f, W - 0.04, H - bin - 0.12, D - 0.1, 0, bin + 0.1 + (H - bin - 0.12) / 2, -0.03);
    slab(M.black, f, W - 0.3, 0.2, 0.01, 0, bin + 0.1 + (H - bin - 0.12) * 0.7, D / 2 - 0.07);
    for (let i = 0; i < 6; i += 1) slab(M.black, f, W - 0.4, 0.015, 0.012, 0, bin + 0.25 + i * 0.05, D / 2 - 0.075);
  });
  // The coffee station: a laminate counter with a brewer, airpots and cups,
  // and the bin at the end of it.
  each('break-coffee', (f) => {
    const { W, D, H } = f;
    const counter = W - 0.36;
    const cx = -W / 2 + counter / 2;
    slab(M.wood, f, counter, H - 0.04, D - 0.02, cx, (H - 0.04) / 2, 0);
    slab(M.white, f, counter + 0.02, 0.04, D, cx, H - 0.02, 0);
    for (let i = 0; i < 2; i += 1) slab(M.chrome, f, 0.12, 0.012, 0.02, cx - counter / 4 + i * counter / 2, H - 0.12, D / 2);
    slab(M.black, f, 0.35, 0.5, 0.35, cx - 0.2, H + 0.25, -0.1);
    slab(M.chrome, f, 0.2, 0.04, 0.2, cx - 0.2, H + 0.08, 0.02);
    for (let i = 0; i < 2; i += 1) put(M.chrome, drum(0.08, 0.08, 0.34, cx + 0.15 + i * 0.2, H + 0.17, -0.12, 12), f);
    for (let i = 0; i < 6; i += 1) put(M.white, drum(0.045, 0.035, 0.1, cx + 0.3, H + 0.05 + i * 0.03, 0.18, 10), f);
    // The trash can: grey, round, a swing lid.
    const tx = W / 2 - 0.18;
    put(M.greyPaint, drum(0.17, 0.15, 0.62, tx, 0.31, 0, 16), f);
    put(M.plastic, drum(0.175, 0.175, 0.06, tx, 0.65, 0, 16), f);
    slab(M.plastic, f, 0.16, 0.12, 0.02, tx, 0.62, 0.1);
  });
  // Two canvas cots, a pillow and a folded blanket each.
  each('break-cot', (f) => {
    const { W, D, H } = f;
    const n = 2;
    const w = Math.min(0.8, W / n - 0.1);
    for (let i = 0; i < n; i += 1) {
      const x = -W / 2 + (W / n) * (i + 0.5);
      slab(M.canvas, f, w, 0.03, D - 0.1, x, H - 0.05, 0);
      for (const sx of [-1, 1]) slab(M.black, f, 0.03, 0.03, D - 0.06, x + sx * (w / 2), H - 0.05, 0);
      for (const sz of [-1, 0, 1]) {
        put(M.black, rod(0.012, [x - w / 2, 0, sz * (D / 2 - 0.15)], [x + w / 2, H - 0.06, sz * (D / 2 - 0.15)]), f);
        put(M.black, rod(0.012, [x + w / 2, 0, sz * (D / 2 - 0.15)], [x - w / 2, H - 0.06, sz * (D / 2 - 0.15)]), f);
      }
      slab(M.white, f, w - 0.15, 0.1, 0.35, x, H + 0.02, -D / 2 + 0.3);
      slab(paint(0x4a5a78, { roughness: 1 }), f, w - 0.1, 0.08, 0.5, x, H + 0.01, D / 2 - 0.4);
    }
  });

  // --- the shop -------------------------------------------------------------------
  each('shop-bench', (f) => {
    const { W, D, H } = f;
    slab(M.wood, f, W, 0.06, D, 0, H - 0.03, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) slab(M.greyPaint, f, 0.06, H - 0.06, 0.06, sx * (W / 2 - 0.08), (H - 0.06) / 2, sz * (D / 2 - 0.08));
    slab(M.wood, f, W - 0.2, 0.03, D - 0.2, 0, 0.22, 0);
    // A pegboard's worth of tools is too much; a vise, a toolbox, a drill.
    slab(M.greyPaint, f, 0.14, 0.12, 0.2, W / 2 - 0.25, H + 0.06, D / 2 - 0.12);
    slab(M.tool, f, 0.5, 0.22, 0.24, -W / 2 + 0.5, H + 0.11, -0.1);
    slab(M.black, f, 0.08, 0.2, 0.26, 0.2, H + 0.1, 0.1);
    for (let i = 0; i < 3; i += 1) slab(M.carton, f, 0.4, 0.3, 0.3, -W / 2 + 0.4 + i * 0.5, 0.39, 0);
  });
  each('shop-shelves', (f) => {
    const { W, D, H } = f;
    slab(M.greyPaint, f, W - 0.05, H - 0.05, 0.02, 0, H / 2, -D / 2 + 0.03);
    for (const sx of [-1, 0, 1]) for (const sz of [-1, 1]) slab(M.greyPaint, f, 0.04, H, 0.04, sx * (W / 2 - 0.03), H / 2, sz * (D / 2 - 0.03));
    const shelves = 5;
    for (let s = 0; s < shelves; s += 1) {
      const y = 0.1 + s * ((H - 0.15) / (shelves - 1));
      slab(M.greyPaint, f, W - 0.02, 0.025, D - 0.02, 0, y, 0);
      if (s === shelves - 1) continue;
      const gap = (H - 0.15) / (shelves - 1);
      for (let i = 0; i < 6; i += 1) {
        if ((i * 3 + s * 5) % 7 === 0) continue;
        const bw = W / 6 - 0.06;
        const hgt = gap * (0.45 + ((i + s) % 3) * 0.12);
        slab(i % 3 ? M.carton : M.rackBlue, f, bw, hgt, D - 0.12, -W / 2 + W / 6 * (i + 0.5), y + 0.012 + hgt / 2, 0);
      }
    }
  });

  // --- the ballroom -----------------------------------------------------------------
  // The linen cage: a wire cage on casters, with linens folded inside.
  each('ballroom-linen-cage', (f) => {
    const { W, D, H } = f;
    slab(M.chrome, f, W, 0.03, D, 0, 0.16, 0);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) caster(f, sx * (W / 2 - 0.08), sz * (D / 2 - 0.08), 0.06);
    const wire = M.chrome;
    const ribs = Math.round(W / 0.1);
    for (let i = 0; i <= ribs; i += 1) {
      const x = -W / 2 + (W * i) / ribs;
      for (const sz of [-1, 1]) put(wire, rod(0.004, [x, 0.17, sz * D / 2], [x, H, sz * D / 2], 4), f);
    }
    for (let i = 0; i <= Math.round(D / 0.1); i += 1) {
      const z = -D / 2 + (D * i) / Math.round(D / 0.1);
      for (const sx of [-1, 1]) put(wire, rod(0.004, [sx * W / 2, 0.17, z], [sx * W / 2, H, z], 4), f);
    }
    for (const y of [0.17, H * 0.5, H]) {
      for (const sz of [-1, 1]) slab(wire, f, W, 0.015, 0.015, 0, y, sz * D / 2);
      for (const sx of [-1, 1]) slab(wire, f, 0.015, 0.015, D, sx * W / 2, y, 0);
    }
    const full = Math.floor((H - 0.2) / 0.042);
    for (let i = 0; i < 3; i += 1) for (let k = 0; k < 3; k += 1) model(linenStack(full - ((i + k) % 3), W / 3 - 0.04, D / 3 - 0.03), { linen: M.linen }, f, -W / 3 + (i * W) / 3, 0.18, -D / 3 + (k * D) / 3);
  });
  // The stage's legs: steel posts on feet, braced, behind the skirt.
  for (const id of ['stage-leg-a', 'stage-leg-b', 'stage-leg-c', 'stage-leg-d']) {
    each(id, (f) => {
      const { W, D, H } = f;
      slab(M.black, f, 0.08, H, 0.08, 0, H / 2, 0);
      slab(M.black, f, W * 0.8, 0.02, D * 0.8, 0, 0.01, 0);
      put(M.black, rod(0.012, [-W * 0.35, 0.02, 0], [0, H * 0.6, 0]), f);
      put(M.black, rod(0.012, [W * 0.35, 0.02, 0], [0, H * 0.6, 0]), f);
    });
  }

  // --- bins -----------------------------------------------------------------------------
  // The dock's dumpster: sloped front, two black lids, the fork pockets.
  each('dock-bin', (f) => {
    const { W, D, H } = f;
    const body = new THREE.BufferGeometry();
    // A prism: full height at the back, lower at the front.
    const back = H - 0.1;
    const front = H * 0.7;
    const hw = W / 2 - 0.05;
    const hd = D / 2 - 0.05;
    const shape = new THREE.Shape([new THREE.Vector2(-hd, 0.1), new THREE.Vector2(hd, 0.1), new THREE.Vector2(hd, front), new THREE.Vector2(-hd, back)]);
    const prism = new THREE.ExtrudeGeometry(shape, { depth: hw * 2, bevelEnabled: false }).rotateY(-Math.PI / 2).translate(hw, 0, 0);
    body.dispose();
    put(M.dumpster, prism, f);
    for (const sx of [-1, 1]) {
      const lid = block(hw - 0.04, 0.03, hd * 2 + 0.06, sx * hw / 2, 0, 0);
      lid.rotateX(Math.atan2(back - front, hd * 2)).translate(0, (back + front) / 2 + 0.02, 0);
      put(M.plastic, lid, f);
      slab(M.dumpster, f, 0.3, 0.12, D - 0.3, sx * (hw + 0.02), H * 0.45, 0);
    }
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) caster(f, sx * (hw - 0.15), sz * (hd - 0.15), 0.05);
  });
  // The lot's skip: tapered, ribbed, rust-brown.
  each('lot-skip', (f) => {
    const { W, D, H } = f;
    const g = new THREE.CylinderGeometry(Math.SQRT1_2, Math.SQRT1_2 * 0.82, 1, 4, 1, true).rotateY(Math.PI / 4);
    g.scale(W - 0.1, H - 0.1, D - 0.1).translate(0, (H - 0.1) / 2 + 0.1, 0);
    const skip = paint(0x8a4a2a, { metalness: 0.2, roughness: 0.8, side: THREE.DoubleSide });
    put(skip, g, f);
    slab(M.skip, f, (W - 0.1) * 0.82, 0.05, (D - 0.1) * 0.82, 0, 0.12, 0);
    for (let i = 0; i < 5; i += 1) {
      const z = -D / 2 + 0.3 + i * ((D - 0.6) / 4);
      for (const sx of [-1, 1]) slab(M.skip, f, 0.08, H - 0.2, 0.1, sx * ((W - 0.1) / 2 - 0.02 - 0.04), H / 2, z);
    }
    for (const sx of [-1, 1]) slab(M.black, f, 0.12, 0.1, D, sx * (W / 2 - 0.4), 0.05, 0);
    // Some of what is in it.
    for (let i = 0; i < 4; i += 1) slab(M.carton, f, 0.5, 0.4, 0.6, -0.6 + i * 0.4, H - 0.3, -0.4 + (i % 2) * 0.6);
  });

  // Pallets: slatted decks on three runners, stacked; the taller one carries a
  // shrink-wrapped load.
  const pallet = (f, y, w, d, z = 0) => {
    const h = 0.14;
    for (const sz of [-1, 0, 1]) slab(M.wood, f, w, h - 0.04, 0.09, 0, y + (h - 0.04) / 2, z + sz * (d / 2 - 0.045));
    const slats = Math.max(3, Math.round(w / 0.16));
    for (let i = 0; i < slats; i += 1) slab(M.wood, f, (w / slats) * 0.7, 0.02, d, -w / 2 + (w / slats) * (i + 0.5), y + h - 0.01, z);
    return y + h;
  };
  // Empty pallets, two stacks of them side by side.
  each('dock-pallet-a', (f) => {
    const stacks = f.D >= 2.1 ? 2 : 1;
    const d = f.D / stacks - 0.1;
    const n = Math.max(1, Math.floor(f.H / 0.14));
    for (let k = 0; k < stacks; k += 1) {
      const z = -f.D / 2 + (f.D / stacks) * (k + 0.5);
      let y = 0;
      for (let i = 0; i < n - k; i += 1) y = pallet(f, y, f.W - 0.08, d, z);
    }
  });
  each('dock-pallet-b', (f) => {
    const w = f.W - 0.05;
    const d = f.D - 0.05;
    const top = pallet(f, 0, w, d);
    const load = f.H - top;
    for (let i = 0; i < 2; i += 1) for (let k = 0; k < 2; k += 1) slab(M.carton, f, w / 2 - 0.03, load - 0.03, d / 2 - 0.03, -w / 4 + (i * w) / 2, top + (load - 0.03) / 2, -d / 4 + (k * d) / 2);
    slab(paint(0xdfe8ec, { roughness: 0.1, transparent: true, opacity: 0.35 }), f, w + 0.01, load, d + 0.01, 0, top + load / 2, 0);
  });
  // The vending machine: a lit glass front of rows of things to buy, the
  // keypad and the slot beside it.
  each('break-vending', (f) => {
    const { W, D, H } = f;
    slab(paint(0x53484f, { roughness: 0.5, metalness: 0.2 }), f, W, H, D, 0, H / 2, 0);
    const gw = W * 0.66;
    const gx = -W / 2 + 0.05 + gw / 2;
    slab(paint(0x101418, { emissive: 0xfff2dc, emissiveIntensity: 0.25, roughness: 0.1 }), f, gw, H * 0.72, 0.02, gx, H * 0.56, D / 2 + 0.01);
    const snack = [0xc8302a, 0xe0b020, 0x2a6ac8, 0x3a8a3a, 0xe07020];
    for (let row = 0; row < 6; row += 1) {
      const y = H * 0.26 + row * (H * 0.66 / 6);
      slab(M.chrome, f, gw - 0.04, 0.01, 0.04, gx, y - 0.02, D / 2 + 0.03);
      for (let c = 0; c < 5; c += 1) slab(paint(snack[(row * 2 + c) % snack.length], { roughness: 0.6 }), f, gw / 5 - 0.03, H * 0.66 / 6 - 0.07, 0.02, gx - gw / 2 + (gw / 5) * (c + 0.5), y + 0.04, D / 2 + 0.035);
    }
    const px = W / 2 - (W - gw - 0.1) / 2 - 0.03;
    slab(M.black, f, W - gw - 0.14, 0.3, 0.02, px, H * 0.62, D / 2 + 0.01);
    slab(M.chrome, f, 0.08, 0.12, 0.02, px, H * 0.5, D / 2 + 0.012);
    slab(M.black, f, gw - 0.1, 0.18, 0.04, gx, H * 0.13, D / 2 + 0.015);
  });

  const meshes = b.build(group);

  return {
    object3D: group,
    meshes,
    /** Which props' boxes this hid. */
    hidden,
    dispose() {
      for (const mesh of meshes) mesh.geometry.dispose();
      for (const d of disposables) d.dispose();
      linenGeometry?.dispose();
    },
  };
}

function mergeAll(list) {
  const geometry = mergeGeometries(list);
  for (const g of list) g.dispose();
  geometry.computeBoundingSphere();
  return geometry;
}
